import React, { useState, useEffect } from "react";
import { Monitor, QrCode, Share, Plus, AlertCircle, ArrowRight } from "lucide-react";

export default function DeviceRestrictionWrapper({ children }) {
  // Synchronous checks to prevent flashing/layout shifts on load
  const checkIsDev = () => {
    return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  };

  const checkIsBypass = () => {
    if (!checkIsDev()) return false;
    const params = new URLSearchParams(window.location.search);
    const bypassParam = params.get("bypass");
    if (bypassParam === "false") {
      localStorage.removeItem("goprotege_bypass_restriction");
      return false;
    }
    return bypassParam === "true" || localStorage.getItem("goprotege_bypass_restriction") === "true";
  };

  const checkIsStandalone = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  };

  const checkIsDesktop = () => {
    const ua = navigator.userAgent;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const width = window.innerWidth;
    return !isMobileUA && width > 768;
  };

  const checkIsIOS = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const [isDev] = useState(checkIsDev());
  const [isBypass, setIsBypass] = useState(checkIsBypass());
  const [isDesktop] = useState(checkIsDesktop());
  const [isStandalone] = useState(checkIsStandalone());
  const [isIOS] = useState(checkIsIOS());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosPrompt, setShowIosPrompt] = useState(false);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);

  useEffect(() => {
    // If not in development, clean up bypass from localStorage immediately
    if (!isDev) {
      localStorage.removeItem("goprotege_bypass_restriction");
    }

    // Capture native install prompt for Android/Chrome
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, [isDev]);

  const handleBypass = () => {
    if (!isDev) return;
    localStorage.setItem("goprotege_bypass_restriction", "true");
    setIsBypass(true);
    window.location.reload();
  };

  const handleNativeInstall = async () => {
    if (!deferredPrompt) {
      // If native PWA prompt is not caught (unsupported browser/stage), show fallback drawer
      setShowAndroidPrompt(true);
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
  };

  // If bypassed (only allowed on dev) or standalone, let them use the app
  if ((isBypass && isDev) || isStandalone) {
    return children;
  }

  // Current URL for QR code generation
  const appUrl = window.location.origin;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(appUrl)}&margin=10`;

  // STATE 1: Desktop / PC Block Screen
  if (isDesktop) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6 font-sans">
        <div className="max-w-4xl w-full bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 p-10 flex flex-col md:flex-row items-center gap-10 md:gap-16 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#00D285] to-[#00B874]" />
          
          {/* Left Column: Text & Logo */}
          <div className="flex-1 text-center md:text-left flex flex-col items-center md:items-start">
            <img src="/brand/logo.png" alt="GoProtege" className="h-16 w-auto object-contain mb-8 mt-2 dark:hidden" />
            <img src="/brand/logo_white.png" alt="GoProtege" className="h-16 w-auto object-contain mb-8 mt-2 hidden dark:block" />
            
            <div className="h-14 w-14 bg-red-50 dark:bg-red-950/30 text-red-500 rounded-2xl flex items-center justify-center mb-6">
              <Monitor size={32} />
            </div>

            <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-4">
              Exclusivo para Celular
            </h2>
            
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-6">
              A Assistência Veicular GoProtege foi desenvolvida para estar ao seu lado em todos os momentos. Para solicitar guinchos, mecânicos, chaveiros e outros atendimentos emergenciais próximos à sua localização, acesse o sistema pelo seu celular e encontre rapidamente a ajuda que você precisa.
            </p>

            {isDev && (
              <button 
                onClick={handleBypass} 
                className="text-xs font-medium text-slate-400 dark:text-slate-500 hover:text-[#00D285] dark:hover:text-[#00D285] transition underline underline-offset-4 cursor-pointer mt-4"
              >
                Entrar em modo de testes (Bypass)
              </button>
            )}
          </div>

          {/* Right Column: QR Code */}
          <div className="flex flex-col items-center shrink-0 w-full md:w-auto bg-slate-50 dark:bg-slate-950 p-6 rounded-2xl border border-slate-100 dark:border-slate-800">
            <img src={qrCodeUrl} alt="QR Code para celular" className="w-48 h-48 object-contain rounded-lg border bg-white border-slate-100 mb-4" />
            
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              <QrCode size={14} className="text-[#00D285]" />
              Aponte a Câmera
            </div>

            <p className="text-xs text-slate-400 dark:text-slate-500 text-center max-w-[200px] leading-normal">
              Escaneie o código acima com seu smartphone para acessar a rede de assistência.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // STATE 2: Mobile Browser (Safari/Chrome) Install Page
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-between p-6 font-sans relative">
      <div className="my-auto max-w-md w-full bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 p-7 flex flex-col items-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-[#00D285]" />
        
        {/* Emblem of GoProtege */}
        <div className="h-20 w-20 bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl flex items-center justify-center mb-6 shadow-inner mt-2">
          <img src="/brand/emblem.png" alt="GoProtege Emblem" className="h-16 w-16 object-contain" />
        </div>

        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3 text-center">
          Assistência Veicular
        </h2>
        
        <p className="text-slate-500 dark:text-slate-400 text-sm text-center leading-relaxed mb-8">
          Tenha acesso rápido a guinchos e serviços de assistência automotiva 24 horas perto de você. Instale o aplicativo e encontre ajuda quando precisar.
        </p>

        {/* Instalação Simplificada: Um único botão que abre fluxo correto ou drawer explicativo de acordo com o OS */}
        <button
          onClick={isIOS ? () => setShowIosPrompt(true) : handleNativeInstall}
          className="w-full bg-[#00D285] hover:bg-[#00B874] text-white font-semibold py-4 rounded-2xl shadow-md transition flex items-center justify-center gap-2 btn-press cursor-pointer"
        >
          Instalar Aplicativo
          <ArrowRight size={18} />
        </button>

        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500 border-t border-slate-100 dark:border-slate-800/80 w-full pt-5 mt-6 justify-center">
          <AlertCircle size={13} className="shrink-0 text-[#00D285]" />
          <span className="text-center font-medium">Serviço exclusivo para assinantes GoProtege.com</span>
        </div>
      </div>

      {isDev && (
        <button 
          onClick={handleBypass} 
          className="mt-4 text-xs font-medium text-slate-400 dark:text-slate-500 hover:text-[#00D285] dark:hover:text-[#00D285] transition underline underline-offset-4 cursor-pointer"
        >
          Ignorar e entrar no modo testes
        </button>
      )}

      {/* iOS Safari Custom Instructions Overlay */}
      {showIosPrompt && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-50 flex flex-col justify-end p-4 fade-in" onClick={() => setShowIosPrompt(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800 text-slate-850 dark:text-slate-200 animate-slide-up flex flex-col items-center gap-4 max-w-md mx-auto w-full relative" onClick={(e) => e.stopPropagation()}>
            <div className="h-10 w-10 bg-emerald-50 dark:bg-emerald-950/20 text-[#00D285] rounded-full flex items-center justify-center animate-bounce mb-2">
              <Share size={20} />
            </div>
            
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Adicionar à Tela de Início</h3>
            
            <div className="space-y-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300 w-full">
              <div className="flex items-start gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">1</span>
                <span>Toque no botão de <strong>Compartilhar</strong> na barra inferior do Safari (o quadrado com uma seta para cima <Share size={14} className="inline mx-0.5" />).</span>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">2</span>
                <span>Role as opções para baixo e toque em <strong>"Adicionar à Tela de Início"</strong> (com o ícone de mais <Plus size={14} className="inline mx-0.5" />).</span>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">3</span>
                <span>Toque em <strong>"Adicionar"</strong> no canto superior direito para criar o atalho.</span>
              </div>
            </div>

            <button 
              onClick={() => setShowIosPrompt(false)}
              className="mt-4 w-full bg-[#00D285] hover:bg-[#00B874] text-white font-semibold py-4 rounded-2xl shadow-md transition cursor-pointer text-center"
            >
              Entendi
            </button>
            
            <div className="text-[10px] text-slate-400 dark:text-slate-500 text-center mt-2 flex flex-col items-center animate-pulse">
              <span>O botão fica no centro da barra de navegação abaixo</span>
              <span className="text-lg font-bold">↓</span>
            </div>
          </div>
        </div>
      )}

      {/* Android Custom Instructions Overlay (Fallback when no native prompt event is caught) */}
      {showAndroidPrompt && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-50 flex flex-col justify-end p-4 fade-in" onClick={() => setShowAndroidPrompt(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800 text-slate-850 dark:text-slate-200 animate-slide-up flex flex-col items-center gap-4 max-w-md mx-auto w-full relative" onClick={(e) => e.stopPropagation()}>
            <div className="h-10 w-10 bg-emerald-50 dark:bg-emerald-950/20 text-[#00D285] rounded-full flex items-center justify-center animate-bounce mb-2">
              <Plus size={20} />
            </div>
            
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Adicionar à Tela de Início</h3>
            
            <div className="space-y-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300 w-full">
              <div className="flex items-start gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">1</span>
                <span>Toque no botão de <strong>Menu (três pontos)</strong> no canto superior direito do seu navegador.</span>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">2</span>
                <span>Selecione a opção <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong>.</span>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00D285]/10 text-[#00D285] text-xs font-bold shrink-0 mt-0.5">3</span>
                <span>Confirme a adição para colocar o atalho na sua tela inicial de aplicativos.</span>
              </div>
            </div>

            <button 
              onClick={() => setShowAndroidPrompt(false)}
              className="mt-4 w-full bg-[#00D285] hover:bg-[#00B874] text-white font-semibold py-4 rounded-2xl shadow-md transition cursor-pointer text-center"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
